import { api } from './api/apiClient';

/**
 * 통합 액션 큐 API 클라이언트 — 대시보드 "오늘의 할 일" (대시보드 재설계 Sprint 3)
 *
 * 백엔드 계약: GET /api/dashboard/action-queue (Sprint 2)
 */

export type ActionQueuePriority = 'critical' | 'high' | 'normal' | 'low';
export type ActionQueueGroupKey = 'customer_request' | 'sla' | 'assigned' | 'routine';
export type ActionQueueSource =
  | 'customer_event'
  | 'sla_breach'
  | 'assigned'
  | 'menu_task_cron'
  | 'install_today'
  | 'install_overdue'
  | 'cash_receipt_request'
  | 'meeting'
  | 'todo';

export interface ActionQueueItem {
  hash: string;
  group: ActionQueueGroupKey;
  source: ActionQueueSource;
  title: string;
  why: string | null;
  priority: ActionQueuePriority;
  menu: string | null;
  reference_type: string | null;
  reference_id: string | null;
  reference_url: string | null;
  due_at: string | null;
  age_hours: number | null;
  overdue: boolean;
  imminent: boolean;
  status: string | null;
  customer_name: string | null;
  event_key: string | null;
  notification_id: string | null;
  created_at: string | null;
  /** 전원 공유 업무 여부 — true 면 한 명이 완료 시 팀 전체 목록에서 제거 (백엔드 산출, S1) */
  shared: boolean;
}

export interface ActionQueueGroup {
  key: ActionQueueGroupKey;
  label: string;
  count: number;
  items: ActionQueueItem[];
}

export interface ActionQueuePayload {
  date: string;
  generated_at: number;
  cache_hit: boolean;
  done_today: number;
  total: number;
  groups: ActionQueueGroup[];
}

export interface QueueBriefing {
  briefing: string | null;
  top_actions: Array<{ hash: string; comment: string }>;
}

/** 완료 실행취소(undo) 결과 — 백엔드 계약(S1): POST /action-queue/undo */
export interface UndoResult {
  /** true: 본인·타인 큐에 항목 복원됨 / false: 복원 안 됨(사유는 reason) */
  restored: boolean;
  /** restored:false 인 경우 사용자 안내 사유 (예: 타 담당자 선완료) */
  reason: string | null;
  /** 서버 기준 오늘 완료 건수(undo 반영 후) */
  done_today: number;
}

interface Envelope<T> {
  success: boolean;
  data: T;
  error?: string;
}

export const actionQueueService = {
  async getQueue(refresh = false): Promise<ActionQueuePayload> {
    const res = await api.get<Envelope<ActionQueuePayload>>(
      `/dashboard/action-queue${refresh ? '?refresh=1' : ''}`,
    );
    if (!res.success) throw new Error(res.error || '액션 큐 조회 실패');
    return res.data;
  },

  /**
   * 완료 처리. idempotent=true 는 "이미 다른 담당자가 완료" — 이 경우 프론트는
   * 실행취소 어포던스를 띄우지 않는다(undo 무효). done_today 는 서버 최신 카운트.
   */
  async done(item: ActionQueueItem): Promise<{ done_today: number; idempotent: boolean }> {
    const res = await api.post<Envelope<{ done_today: number; idempotent?: boolean }>>(
      '/dashboard/action-queue/done',
      {
        hash: item.hash,
        source: item.source,
        reference_type: item.reference_type,
        reference_id: item.reference_id,
        title: item.title,
        notification_id: item.notification_id,
      },
    );
    if (!res.success) throw new Error(res.error || '완료 처리 실패');
    return { done_today: res.data?.done_today ?? 0, idempotent: !!res.data?.idempotent };
  },

  /**
   * 완료 실행취소 — body 는 `hash` 만 전송한다.
   * 복원 대상 이벤트 정체성은 서버가 본인 done 행에서 파생하므로(신뢰 경계, S1 [C-22]),
   * notification_id·reference_* 등 나머지 필드는 전송하지 않는다.
   */
  async undo(item: ActionQueueItem): Promise<UndoResult> {
    const res = await api.post<Envelope<UndoResult>>('/dashboard/action-queue/undo', {
      hash: item.hash,
    });
    if (!res.success) throw new Error(res.error || '완료 취소 실패');
    return {
      restored: !!res.data?.restored,
      reason: res.data?.reason ?? null,
      done_today: res.data?.done_today ?? 0,
    };
  },

  async snooze(item: ActionQueueItem): Promise<void> {
    const res = await api.post<Envelope<unknown>>('/dashboard/action-queue/snooze', {
      hash: item.hash,
      source: item.source,
      reference_type: item.reference_type,
      reference_id: item.reference_id,
      title: item.title,
    });
    if (!res.success) throw new Error(res.error || '숨김 처리 실패');
  },

  async getBriefing(refresh = false): Promise<QueueBriefing> {
    const res = await api.get<Envelope<QueueBriefing>>(
      `/dashboard/action-queue/briefing${refresh ? '?refresh=1' : ''}`,
    );
    if (!res.success) throw new Error(res.error || '브리핑 조회 실패');
    return { briefing: res.data?.briefing ?? null, top_actions: res.data?.top_actions ?? [] };
  },
};

export default actionQueueService;
